import { OverlaySizeConfig } from '@angular/cdk/overlay';
import {
  DestroyRef,
  Directive,
  inject,
  Injectable,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  OverlayPosition,
} from '../../../core/overlay/overlay-directives';
import { SelectController } from '../select-controller.service';

export interface OpenStrategyOptions {
  overlayWidth?: number | string;
  overlayMinWidth?: number | string;
  overlayPosition?: OverlayPosition[];
  autoClose?: boolean;
}

export interface RegisterOptions {
  inlineTriggerOrigin: OverlayOriginDirective;
  dropdownConnectedOverlay: ConnectedOverlayDirective;
  dropdownTemplate: TemplateRef<void>;
  dropdownTemplateVcRef: ViewContainerRef;
  triggerOverlay: ConnectedOverlayDirective;
  overlayTriggerOrigin$: Subject<OverlayOriginDirective | null>;
}

export abstract class OpenStrategy {
  protected inlineTriggerOrigin: OverlayOriginDirective;
  protected dropdownConnectedOverlay: ConnectedOverlayDirective;
  protected dropdownTemplate: TemplateRef<void>;
  protected dropdownTemplateVcRef: ViewContainerRef;
  protected triggerOverlay: ConnectedOverlayDirective;
  protected overlayTriggerOrigin$: Subject<OverlayOriginDirective | null>;
  protected options: OpenStrategyOptions = {};

  // Called by select to register overlays
  public register(options: RegisterOptions) {
    Object.assign(this, options);
  }

  public setOptions(options: OpenStrategyOptions) {
    this.options = options;
  }

  // Called after overlays registered
  public abstract init(): void;
}

@Injectable()
export class AlwaysOpenStrategy extends OpenStrategy {
  public override init() {
    this.dropdownTemplateVcRef.createEmbeddedView(this.dropdownTemplate);
  }
}

@Directive({
  standalone: true,
  selector: 'rt-select2[alwaysOpen]',
  providers: [{ provide: OpenStrategy, useClass: AlwaysOpenStrategy }],
})
export class AlwaysOpenStrategyDirective {}

@Injectable()
export class InlineOpenStrategy extends OpenStrategy {
  private selectController = inject(SelectController);
  private destroyRef = inject(DestroyRef);

  public override init() {
    this.dropdownConnectedOverlay.hasBackdrop = true;
    this.dropdownConnectedOverlay.autoCloseOnUnpreventedClick = false;
    this.dropdownConnectedOverlay.origin = this.inlineTriggerOrigin;
    this.dropdownConnectedOverlay.width = this.options.overlayWidth!;
    this.dropdownConnectedOverlay.minWidth = this.options.overlayMinWidth!;
    if (this.options.overlayPosition) {
      this.dropdownConnectedOverlay.position = this.options.overlayPosition;
    }

    this.selectController.isOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isOpen => {
      const { width: previousWidth, minWidth: previousMinWidth } = this.dropdownConnectedOverlay;
      const width = this.options.overlayWidth ?? this.getDropdownWidth();
      const minWidth = this.options.overlayMinWidth;
      this.dropdownConnectedOverlay.width = width;
      this.dropdownConnectedOverlay.minWidth = minWidth!;
      if (previousWidth !== width) this.updateOverlayState({ width });
      if (previousMinWidth !== minWidth) this.updateOverlayState({ minWidth });
      this.dropdownConnectedOverlay.open = isOpen;
    });
    this.dropdownConnectedOverlay.backdropClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectController.toggle(false);
      });

    this.selectController.select$
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.options.autoClose) return;

        this.selectController.isOpen$.next(false);
      });
  }

  private getDropdownWidth() {
    const triggerEl: HTMLElement = this.inlineTriggerOrigin.elementRef.nativeElement;
    return triggerEl.clientWidth;
  }

  private updateOverlayState(state: OverlaySizeConfig) {
    const { overlayRef } = this.dropdownConnectedOverlay;
    if (!overlayRef) return;

    overlayRef.updateSize(state);
    this.dropdownConnectedOverlay.updatePositionWithoutRedrawing();
  }
}
